<template>
  <div id="corporation-page" class="pb-6">
    <intro-tabs value="corporation" />
    <section class="banner key-vision h-70dvh lg:h-30dvw flex items-center -mx-4">
      <div class="banner-content text-white px-8 lg:max-w-554px mx-auto px-4">
        <h2 class="text-center mb-20 text-48px leading-67px lg:(text-56px leading=86px)">{{ $t('footer_vender_join_us') }}</h2>
        <p class="text-center text-sm tracking-0.5px">
          <span class="whitespace-pre-line">{{ $t('recruit_supplier_page_desc') }}</span>
        </p>
      </div>
    </section>
    <section class="what-is-niceday lg:container mx-auto my-6 px-4">
      <h3 class="heading">{{ $t('recruit_supplier_brand_intro_title') }}</h3>
      <div class="content lg:flex-row flex flex-col-reverse gap-8">
        <carousel loop autoplay :gap="0" :data="FIGURE_LIST" class="-mx-4 lg:w-3/5 lg:mx-0">
          <template #default="{ item }">
            <nd-img :src="item" alt="niceday-image" class="w-full" />
          </template>
        </carousel>
        <div class="<lg:container <lg:mx-auto lg:w-2/5">
          <dl class="brief">
            <dt class="whitespace-pre-line">{{ $t('recruit_supplier_brand_intro_desc') }}</dt>
          </dl>
        </div>
      </div>
    </section>
    <section class="benefit container mx-auto my-6 px-4">
      <h3 class="heading">{{ $t('recruit_supplier_benefit_title') }}</h3>
      <div class="content grid grid-cols-1 lg:grid-cols-2 gap-60px mb-60px">
        <div v-for="b in BENEFIT_LIST" :key="b.title" class="benefit-item">
          <nd-img :src="b.icon" :alt="b.title" class="w-50px h-50px flex-shrink-0" />
          <dl class="brief">
            <dt>{{ b.title }}</dt>
            <dd>{{ b.desc }}</dd>
          </dl>
        </div>
      </div>
    </section>
    <section class="faq my-3 py-3 w-100dvw px-4 bg-gray-20">
      <h3 class="heading">{{ $t('recruit_supplier_service_title') }}</h3>
      <div class="content container mx-auto mb-20">
        <dl class="brief">
          <template v-for="item in FAQ_LIST" :key="item.title">
            <dt>{{ item.title }}</dt>
            <dd v-for="p in item.desc" :key="p" class="whitespace-pre-line">
              <svg-icon v-if="item.icon" :name="item.icon" class="flex-shrink-0" :class="item.iconClass" />
              {{ p }}
            </dd>
          </template>
        </dl>
      </div>
    </section>
    <section class="introduction container mx-auto py-6 px-4">
      <h3 class="heading">{{ $t('recruit_supplier_service_introduction_title') }}</h3>
      <div class="content mb-20">
        <dl class="brief">
          <template v-for="item in INTRODUCTION_LIST" :key="item.title">
            <div class="divider mx-auto my-25px lg:my-0">
              <hr />
            </div>
            <div class="intro-group lg:flex lg:items-center lg:gap-50px">
              <dt class="min-w-max">{{ item.title }}</dt>
              <div>
                <dd v-for="p in item.desc" :key="p">
                  {{ p }}
                </dd>
              </div>
            </div>
          </template>
          <div class="divider mx-auto my-0 my-25px lg:my-0">
            <hr />
          </div>
        </dl>
      </div>
    </section>
    <section class="join-us px-12 pt-18 pb-6 lg:container lg:mx-auto lg:pt-224px lg:pb-20 lg:mb-20">
      <div class="content">
        <p class="text-white text-lg mb-3 lg:text-56px lg:leading-86px lg:mb-84px">
          {{ $t('recruit_supplier_service_join_title') }} <br />
          {{ $t('recruit_supplier_service_join_subtitle') }}
        </p>
        <a :href="JOIN_FORM_URL" target="_blank" rel="noopener noreferrer">
          <nd-button class="cta-btn" type="primary">{{ $t('recruit_supplier_service_join_action_btn') }}</nd-button>
        </a>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import IntroTabs from '@/components/common/intro-tabs.vue'
import carousel from '@/components/common/carousel.vue'

definePageMeta({
  middleware: ['check-region'],
})

const { t } = useI18n()

const FIGURE_LIST = [
  '/images/corporation/figure_1.webp',
  '/images/corporation/figure_2.webp',
  '/images/corporation/figure_3.webp',
  '/images/corporation/figure_4.webp',
  '/images/corporation/figure_5.webp',
  '/images/corporation/figure_6.webp',
] as const

const BENEFIT_LIST = computed(() => [
  {
    icon: '@/assets/images/corporation/icon_benefit_1.svg',
    title: t('recruit_supplier_benefit_targeting_title'),
    desc: t('recruit_supplier_benefit_targeting_desc'),
  },
  {
    icon: '@/assets/images/corporation/icon_benefit_2.svg',
    title: t('recruit_supplier_benefit_payment_options_title'),
    desc: t('recruit_supplier_benefit_payment_options_desc'),
  },
  {
    icon: '@/assets/images/corporation/icon_benefit_3.svg',
    title: t('recruit_supplier_benefit_wholeseller_title'),
    desc: t('recruit_supplier_benefit_wholeseller_desc'),
  },
  {
    icon: '@/assets/images/corporation/icon_benefit_4.svg',
    title: t('recruit_supplier_benefit_pms_title'),
    desc: t('recruit_supplier_benefit_pms_desc'),
  },
  {
    icon: '@/assets/images/corporation/icon_benefit_5.svg',
    title: t('recruit_supplier_benefit_customer_friendly_title'),
    desc: t('recruit_supplier_benefit_customer_friendly_desc'),
  },
  {
    icon: '@/assets/images/corporation/icon_benefit_6.svg',
    title: t('recruit_supplier_benefit_marketing_addon_title'),
    desc: t('recruit_supplier_benefit_marketing_addon_desc'),
  },
])

const FAQ_LIST = computed(
  () =>
    [
      {
        title: t('recruit_supplier_service_charging_title'),
        desc: [t('recruit_supplier_service_charging_desc')],
        icon: false,
      },
      {
        title: t('recruit_supplier_service_activity_fits_title'),
        desc: [t('recruit_supplier_service_activity_fits_desc')],
        icon: false,
      },
      {
        title: t('recruit_supplier_service_activity_prohibited_title'),
        desc: [
          t('recruit_supplier_service_activity_prohibited_term1'),
          t('recruit_supplier_service_activity_prohibited_term2'),
          t('recruit_supplier_service_activity_prohibited_term3'),
          t('recruit_supplier_service_activity_prohibited_term4'),
          t('recruit_supplier_service_activity_prohibited_term5'),
        ],
        icon: 'cancel',
        iconClass: 'text-red-50 w-6 h-6 mr-5',
      },
    ] as const
)

const INTRODUCTION_LIST = computed(() => [
  {
    title: t('recruit_supplier_service_introduction_management_title'),
    desc: [
      t('recruit_supplier_service_introduction_management_contract'),
      t('recruit_supplier_service_introduction_management_launch'),
      t('recruit_supplier_service_introduction_management_manage'),
    ],
  },
  {
    title: t('recruit_supplier_service_introduction_cusomter_service_title'),
    desc: [t('recruit_supplier_service_introduction_cusomter_service_desc')],
  },
  {
    title: t('recruit_supplier_service_introduction_campaign_title'),
    desc: [t('recruit_supplier_service_introduction_campaign_desc')],
  },
])

const JOIN_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdZGmPUt9-KAsZ5potMXiLFlySwg9njTbnVW6R_SgCizuqRWw/viewform'
</script>

<style lang="scss" scoped>
#corporation-page {
  .banner {
    background: url(/images/corporation/banner_mobile.webp) center center / cover no-repeat;
    @screen lg {
      background: url(/images/corporation/banner_desktop.webp) center center / cover no-repeat;
    }
  }
  .heading {
    @apply flex justify-center items-center flex-col text-30px leading-47px text-center mb-50px mt-70px font-normal text-black-50;
    @apply lg:text-32px lg:mb-90px lg:mt-140px
    letter-spacing: 0.67px;

    &::after {
      @apply block bg-yellow-50 h-1 w-10 mt-14px;
      content: '';
    }
  }
  .brief {
    dt {
      @apply text-base leading-6 my-6 text-black-50 tracking-0.62px;
      @apply lg:text-xl lg:leading-30px lg:my-8;
    }
    dd {
      @apply text-sm lg:text-base text-black-20 tracking-0.5px;
    }
  }
  .faq .brief {
    dt {
      @apply mt-12 mb-6;
    }
    dd {
      @apply mb-6 flex items-center;
    }
  }
  .introduction .brief {
    dt {
      @apply mb-3 lg:my-50px;
    }
    dd {
      @apply lg:my-0;
    }
  }
  .join-us {
    background: url(/images/corporation/join.webp) center center / cover no-repeat;
  }
  .cta-btn {
    @apply py-4 px-8 tracking-1.2px leading-[1.15];
  }
}
</style>
